import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    FaSave, FaTimes, FaPlus, FaTrash, FaHeartbeat, FaMapMarkerAlt,
    FaLink, FaFileMedical, FaCalendarAlt, FaCheckCircle, FaCamera, FaFileUpload, FaUserMd, FaCheck
} from 'react-icons/fa';
import Nav from './Nav';
import FeedbackModal from './shared/FeedbackModal';
import CountrySelector from './common/CountrySelector';
import userService from '../services/userService';
import './ProfilePatientEdit.css';

const ProfilePatientEdit = () => {
    const { user, token, refreshUser } = useAuth();
    const navigate = useNavigate();
    const avatarInputRef = useRef(null);
    const [avatarPreview, setAvatarPreview] = useState('/patient_avatar.png');
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState({ isOpen: false, title: '', message: '', type: 'success' });

    const countries = [
        { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
        { code: 'ES', name: 'España', flag: '🇪🇸' },
        { code: 'MX', name: 'México', flag: '🇲🇽' },
        { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
        { code: 'CL', name: 'Chile', flag: '🇨🇱' },
        { code: 'PE', name: 'Perú', flag: '🇵🇪' },
        { code: 'EC', name: 'Ecuador', flag: '🇪🇨' },
        { code: 'VE', name: 'Venezuela', flag: '🇻🇪' },
        { code: 'US', name: 'Estados Unidos', flag: '🇺🇸' },
        { code: 'PT', name: 'Portugal', flag: '🇵🇹' },
        { code: 'FR', name: 'Francia', flag: '🇫🇷' },
        { code: 'IT', name: 'Italia', flag: '🇮🇹' },
        { code: 'DE', name: 'Alemania', flag: '🇩🇪' }
    ].sort((a, b) => a.name.localeCompare(b.name));

    const [formData, setFormData] = useState({
        name: user?.profile ? `${user.profile.firstName || ''} ${user.profile.lastName || ''}`.trim() : '',
        condition: 'Paciente en EcoScienceInnova',
        country: user?.profile?.country || 'CO',
        city: '',
        age: '54 años',
        description: user?.profile?.bio || '',
        interests: ['Nutrición', 'Yoga', 'Meditación', 'Salud Mental'],
        treatmentDays: '128',
        appointments: '12',
        symptoms: '42'
    });

    const [newInterest, setNewInterest] = useState('');

    useEffect(() => {
        if (user?.profile) {
            setFormData(prev => ({
                ...prev,
                name: `${user.profile.firstName || ''} ${user.profile.lastName || ''}`.trim(),
                description: user.profile.bio || '',
                country: user.profile.country || 'CO'
            }));
            if (user.profile.photoUrl) {
                const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
                setAvatarPreview(user.profile.photoUrl.startsWith('http') ? user.profile.photoUrl : `${backendUrl}${user.profile.photoUrl.startsWith('/') ? '' : '/'}${user.profile.photoUrl}`);
            }
        }
    }, [user]);

    const goBack = () => {
        // Redirigir al feed de comunidad para pacientes
        navigate('/community-feed');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAvatarClick = () => {
        avatarInputRef.current.click();
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDocUpload = (histId, e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                history: prev.history.map(h =>
                    h.id === histId ? { ...h, docName: file.name } : h
                )
            }));
        }
    };

    const handleAddInterest = () => {
        if (newInterest.trim() && !formData.interests.includes(newInterest.trim())) {
            setFormData(prev => ({
                ...prev,
                interests: [...prev.interests, newInterest.trim()]
            }));
            setNewInterest('');
        }
    };

    const handleRemoveInterest = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            interests: prev.interests.filter(t => t !== tagToRemove)
        }));
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const data = new FormData();

            // Split name if needed (backend expects firstName and lastName)
            const nameParts = formData.name.split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';

            data.append('firstName', firstName);
            data.append('lastName', lastName);
            data.append('bio', formData.description);
            data.append('country', formData.country);
            data.append('profession', formData.condition || 'Paciente');
            data.append('specialties', formData.interests.join(','));
            // Patient specific condition field? Backend controller handles bio as description
            // For now, let's keep it consistent with what we have.

            if (selectedFile) {
                data.append('photo', selectedFile);
            }

            await userService.updateProfile(data, token);
            await refreshUser();

            setFeedback({
                isOpen: true,
                title: 'Perfil Guardado',
                message: 'Tu perfil de salud ha sido actualizado correctamente.',
                type: 'success'
            });
            setTimeout(() => {
                goBack();
            }, 2000);
        } catch (err) {
            console.error('Error saving profile:', err);
            const errorMsg = err.response?.data?.msg || err.response?.data?.error || err.message || 'No se pudo guardar el perfil. Intenta de nuevo.';
            setFeedback({
                isOpen: true,
                title: 'Error',
                message: errorMsg,
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="ppe-page">
            <Nav />
            <div className="ppe-container">
                <header className="ppe-header">
                    <div className="ppe-header-text">
                        <h1>Configuración de Perfil de Salud</h1>
                        <p>Diligencia tu información para EcoScienceInnova</p>
                    </div>
                    <div className="ppe-header-actions">
                        <button className="ppe-btn-cancel" onClick={goBack}>
                            <FaTimes /> Cancelar
                        </button>
                        <button className="ppe-btn-save" onClick={handleSave}>
                            <FaSave /> Guardar Cambios
                        </button>
                    </div>
                </header>

                <div className="ppe-grid">
                    {/* AVATAR */}
                    <div className="ppe-section ppe-avatar-section">
                        <h2 className="ppe-section-title">Foto de Perfil</h2>
                        <div className="ppe-avatar-upload-container" onClick={handleAvatarClick}>
                            <img src={avatarPreview} alt="Preview" className="ppe-avatar-preview" />
                            <div className="ppe-avatar-overlay">
                                <FaCamera />
                                <span>Cambiar Foto</span>
                            </div>
                            <input
                                type="file"
                                ref={avatarInputRef}
                                onChange={handleAvatarChange}
                                style={{ display: 'none' }}
                                accept="image/*"
                            />
                        </div>
                    </div>

                    {/* INFORMACIÓN PERSONAL */}
                    <div className="ppe-section ppe-full">
                        <h2 className="ppe-section-title">Información Personal</h2>
                        <div className="ppe-row">
                            <div className="ppe-form-group">
                                <label>Nombre Completo</label>
                                <input
                                    type="text" name="name"
                                    value={formData.name} onChange={handleInputChange}
                                />
                            </div>
                            <div className="ppe-form-group">
                                <label>Condición / Estado de Salud</label>
                                <input
                                    type="text" name="condition"
                                    value={formData.condition} onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        <div className="ppe-row">
                            <div className="ppe-form-group">
                                <label><FaMapMarkerAlt /> País</label>
                                <CountrySelector
                                    value={formData.country}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="ppe-form-group">
                                <label>Ciudad</label>
                                <input
                                    type="text"
                                    name="city"
                                    placeholder="Ej: Madrid"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        <div className="ppe-row">
                            <div className="ppe-form-group">
                                <label>Edad</label>
                                <input type="text" name="age" value={formData.age} onChange={handleInputChange} />
                            </div>
                        </div>
                        <div className="ppe-form-group">
                            <label>Descripción de tu Perfil</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Cuéntanos un poco sobre ti..."
                                rows={4}
                            />
                        </div>
                    </div>

                    {/* TEMAS DE INTERÉS */}
                    <div className="ppe-section ppe-full">
                        <h2 className="ppe-section-title">Temas de Interés</h2>

                        <div className="ppe-interests-input-group">
                            <div className="ppe-form-group flex-1">
                                <input
                                    type="text"
                                    value={newInterest}
                                    onChange={(e) => setNewInterest(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddInterest()}
                                    placeholder="Añade un tema de interés..."
                                />
                            </div>
                            <button className="ppe-btn-add-tag" onClick={handleAddInterest}>
                                <FaPlus /> añadir tema
                            </button>
                        </div>

                        <div className="ppe-interests-container">
                            {formData.interests.map((tag, i) => (
                                <span key={i} className="ppe-tag-edit">
                                    {tag} <FaTrash className="ppe-remove-tag" onClick={() => handleRemoveInterest(tag)} />
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <FeedbackModal
                isOpen={feedback.isOpen}
                title={feedback.title}
                message={feedback.message}
                type={feedback.type}
                onClose={() => setFeedback({ ...feedback, isOpen: false })}
            />
        </div>
    );
};

export default ProfilePatientEdit;
